import { formatEndurance, fuelSummary, provisionsSummary, type FuelSummary, type ProvisionsSummary } from "./endurance";
import type { NpcHullTierLabel } from "./types";

/**
 * Don't let a captain leave the dock unable to come back (16.104, Don's
 * direction).
 *
 * 16.103 named this gap and Don took it: *"Add a check when undocking with low
 * fuel or low provisions. Have the captain confirm if they want to undock or
 * open the market or their base cargo to get more on to the ship."*
 *
 * The dock is the only place either pool can be refilled, which is exactly why
 * this belongs here and nowhere else. Four jumps out, the same warning is only
 * bad news; at the dock it is a fixable problem, and the fix is one click away.
 *
 * ## Thresholds, and why they are not the same as the readouts'
 *
 * `PROVISIONS_LOW_HOURS` (6h) is right for a HUD tint -- it is a passive nudge
 * you glance at. A gate that stops the captain has to justify itself, so it is
 * deliberately more conservative in one direction and less in the other:
 *
 * - **Fuel** warns below `UNDOCK_MIN_JUMPS` (3) rather than at "stranded". At
 *   the point of being stranded it is already too late to be a warning -- the
 *   ship simply cannot leave. Three jumps is enough to reach somewhere and get
 *   back, which is the real question being asked.
 * - **Provisions** warns below `UNDOCK_MIN_HOURS` (12) rather than 6, because
 *   undocking commits to a trip: six hours is fine to *sit* on and thin to
 *   *leave* on. A full tank is 24h, so 12h means "you are under half".
 *
 * Never blocks. A captain who wants to run a two-jump errand on fumes is making
 * a legitimate call, and a modal that refuses would be worse than the strand.
 */

export const UNDOCK_MIN_JUMPS = 3;
export const UNDOCK_MIN_HOURS = 12;

export interface UndockWarning {
  /** Present at all only when something is actually worth stopping for. */
  needed: boolean;
  fuel: FuelSummary;
  provisions: ProvisionsSummary;
  lowFuel: boolean;
  lowProvisions: boolean;
  /** Cannot leave at all -- a strictly worse case than merely low. */
  strandedNow: boolean;
  /** Ordered reasons, worst first, for the dialog to list. */
  reasons: string[];
}

export function undockWarning(
  hullTierLabel: NpcHullTierLabel,
  crew: number,
  fuelOnBoard: number,
  fuelCapacity: number,
  provisionsOnBoard: number,
  provisionsCapacity: number
): UndockWarning {
  const fuel = fuelSummary(hullTierLabel, fuelOnBoard, fuelCapacity);
  const provisions = provisionsSummary(crew, provisionsOnBoard, provisionsCapacity);
  const lowFuel = fuel.fastTravelsLeft < UNDOCK_MIN_JUMPS;
  const lowProvisions = Number.isFinite(provisions.hoursLeft) && provisions.hoursLeft < UNDOCK_MIN_HOURS;

  const reasons: string[] = [];
  if (fuel.stranded) {
    reasons.push(`Not enough fuel to travel at all — a jump costs ${fuel.fastTravelCost} and you have ${Math.round(fuel.onBoard)}.`);
  } else if (lowFuel) {
    reasons.push(
      `Only ${fuel.fastTravelsLeft} jump${fuel.fastTravelsLeft === 1 ? "" : "s"} of fuel — enough to leave, maybe not to return.`
    );
  }
  // Only when somebody is actually eating. A crewless hull with an empty larder
  // is not starving, and warning about it would be noise on a hull that can
  // never mutiny -- caught by the test for exactly that case.
  const anyoneEating = provisions.crew > 0;
  if (anyoneEating && provisions.onBoard <= 0) {
    reasons.push("No provisions aboard — the crew are already counting down to mutiny.");
  } else if (anyoneEating && lowProvisions) {
    // Uses the same formatter the readouts do, rather than rounding to whole
    // hours here: "1 hours" was what shipped for a moment, and "37 min" is both
    // correct and more alarming than "1 hour", which is the point of the dialog.
    reasons.push(`Provisions for about ${formatEndurance(provisions.hoursLeft)} — the crew eat whether you are flying or not.`);
  }

  return {
    needed: reasons.length > 0,
    fuel,
    provisions,
    lowFuel: lowFuel || fuel.stranded,
    lowProvisions: anyoneEating && (lowProvisions || provisions.onBoard <= 0),
    strandedNow: fuel.stranded,
    reasons
  };
}
